import React, { useMemo, useState } from 'react';
import { Box, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getChatSuggestionChipSx } from '../Onboarding/chat/chatVisual';
import { CHAT_V2_MARQUEE_ROWS, CHAT_V2_MOBILE_PROMPTS } from './chatV2Constants';

/** Original loop was 48s; 40% slower pace → 48 / 0.6 = 80s. */
export const CHAT_MARQUEE_ROW_DURATION_S = 80;
const ROW_STAGGER_S = 4 / 0.6;

function buildTrack(items, repeat = 2) {
  return Array.from({ length: repeat }, () => items).flat();
}

/**
 * Slow horizontal marquee rows for the V2/V3 chat empty state.
 */
export default function ChatPromptMarquee({ prompts = CHAT_V2_MARQUEE_ROWS, onSelectPrompt, disabled = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const chipSx = {
    ...getChatSuggestionChipSx(theme, { isHero: true }),
    flexShrink: 0,
  };
  const [paused, setPaused] = useState(false);
  const rows = useMemo(
    () => prompts.map((row, index) => ({
      id: `marquee-row-${index}`,
      direction: index % 2 === 0 ? 'normal' : 'reverse',
      duration: CHAT_MARQUEE_ROW_DURATION_S + index * ROW_STAGGER_S,
      items: buildTrack(row),
      rawItems: row,
    })),
    [prompts]
  );

  if (isMobile) {
    // Use the curated mobile subset when the default CHAT_V2_MARQUEE_ROWS are passed in;
    // if a caller supplies custom prompts, flatten them as before.
    const mobileItems =
      prompts === CHAT_V2_MARQUEE_ROWS ? CHAT_V2_MOBILE_PROMPTS : prompts.flat();
    return (
      <Box
        data-testid="chat-prompt-marquee"
        data-paused="false"
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          columnGap: 1,
          rowGap: 1.25,
          py: 0.5,
          justifyContent: 'center',
        }}
      >
        {mobileItems.map((label, index) => (
          <Chip
            key={`mobile-prompt-${label}-${index}`}
            label={label}
            clickable
            disabled={disabled}
            onClick={() => onSelectPrompt?.(label)}
            variant="outlined"
            sx={{ ...chipSx, flexShrink: 1, maxWidth: '100%' }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box
      data-testid="chat-prompt-marquee"
      data-paused={paused ? 'true' : 'false'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        width: '100%',
        maxWidth: 920,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        overflow: 'hidden',
        py: 0.5,
        maskImage: 'linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)',
        WebkitMaskImage:
          'linear-gradient(90deg, transparent 0%, #000 8%, #000 92%, transparent 100%)',
        '@media (prefers-reduced-motion: reduce)': {
          maskImage: 'none',
          WebkitMaskImage: 'none',
        },
      }}
    >
      {rows.map((row) => (
        <Box
          key={row.id}
          data-testid={row.id}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            py: 0.25,
            '@media (prefers-reduced-motion: reduce)': {
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { display: 'none' },
            },
          }}
        >
          <Box
            data-marquee-track=""
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              width: 'max-content',
              pr: 1,
              animation: `chatMarquee${row.direction === 'reverse' ? 'Reverse' : ''} ${row.duration}s linear infinite`,
              animationPlayState: paused ? 'paused' : 'running',
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                flexWrap: 'nowrap',
              },
              '@keyframes chatMarquee': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' },
              },
              '@keyframes chatMarqueeReverse': {
                '0%': { transform: 'translateX(-50%)' },
                '100%': { transform: 'translateX(0)' },
              },
            }}
          >
            {row.items.map((label, index) => (
              <Chip
                key={`${row.id}-${label}-${index}`}
                label={label}
                clickable
                disabled={disabled}
                onClick={() => onSelectPrompt?.(label)}
                variant="outlined"
                sx={chipSx}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
